import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../utils/api";
import Icon from "./common/icons";
import "../styles/SectionUpdateModal.css";

const LEVEL_LABELS = {
  basic_education: "Grade Level",
  higher_education: "College",
  graduate_school: "Program",
  other: "Personnel Type",
};

const SectionUpdateModal = ({ show, status, onConfirmed }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [orgData, setOrgData] = useState(null);
  const [departmentId, setDepartmentId] = useState("");
  const [levelId, setLevelId] = useState("");
  const [sectionId, setSectionId] = useState("");
  // With a class on record the student is first asked to keep it; "Change my class" opens the form
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (show) setChanging(false);
  }, [show]);

  const fetchOrgData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/auth/user-organizational-info/");
      setOrgData(response.data);
    } catch (err) {
      console.error("Failed to load organizational options:", err);
      setError("Could not load your school options. Please refresh and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      fetchOrgData();
    }
  }, [show, fetchOrgData]);

  useEffect(() => {
    if (!orgData?.current) return;
    const current = orgData.current;
    if (current.department?.id) {
      setDepartmentId(String(current.department.id));
    }
    if (current.organizational_level?.id) {
      setLevelId(String(current.organizational_level.id));
    }
    if (current.section?.id) {
      setSectionId(String(current.section.id));
    }
  }, [orgData]);

  const departments = orgData?.available?.departments || [];
  const selectedDepartment = departments.find(
    (d) => String(d.id) === String(departmentId),
  );
  const deptType = selectedDepartment?.type;
  const levelLabel = deptType ? LEVEL_LABELS[deptType] : "Level";

  const organizationalLevels = useMemo(() => {
    if (!departmentId || !orgData?.available?.organizational_levels) return [];
    const levels = orgData.available.organizational_levels;
    return levels[departmentId] || levels[Number(departmentId)] || [];
  }, [departmentId, orgData]);

  const sections = useMemo(() => {
    if (!levelId || !orgData?.available?.sections) return [];
    const allSections = orgData.available.sections;
    return allSections[levelId] || allSections[Number(levelId)] || [];
  }, [levelId, orgData]);

  const selectedLevel = organizationalLevels.find(
    (l) => String(l.id) === String(levelId),
  );
  const selectedSection = sections.find(
    (s) => String(s.id) === String(sectionId),
  );

  const handleDepartmentChange = (value) => {
    setDepartmentId(value);
    setLevelId("");
    setSectionId("");
  };

  const handleLevelChange = (value) => {
    setLevelId(value);
    setSectionId("");
  };

  const canSubmit = Boolean(departmentId && levelId && sectionId && !submitting);

  const confirmSection = async (id) => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await api.post("/api/confirm-section/", {
        section_id: parseInt(id, 10),
      });
      toast.success("Saved. You can keep using ANNA.");
      onConfirmed?.(response.data);
    } catch (err) {
      const message =
        err.response?.data?.error ||
        "Something went wrong while saving. Please try again.";
      setError(message);
      toast.error(message);
      // The class on record can't be kept (e.g. its department closed): pick another
      setChanging(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    confirmSection(sectionId);
  };

  const currentSchoolYear = status?.current_school_year || "this school year";
  const currentSection = status?.current_section;
  const asking = Boolean(currentSection?.section_id) && !changing;
  const currentClass = currentSection
    ? [
        currentSection.department_name,
        currentSection.organizational_level_name,
        currentSection.section_name,
      ].filter(Boolean).join(" · ")
    : "";

  const academicDepartments = departments.filter((d) =>
    ["basic_education", "higher_education", "graduate_school"].includes(d.type),
  );
  const adminDepartments = departments.filter((d) => d.type === "other");

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
      backdropClassName="section-update-backdrop"
      aria-labelledby="section-update-title"
    >
      <Modal.Header>
        <Modal.Title id="section-update-title">
          {asking ? "Check your class" : "Update your class"}
        </Modal.Title>
      </Modal.Header>

      {asking ? (
        <>
          <Modal.Body>
            <div className="am-stack">
              <div className="am-intro">
                <span className="am-intro-icon">
                  <Icon name="graduation-cap" size={28} />
                </span>
                <h3 className="am-intro-title">School year {currentSchoolYear}</h3>
                <p className="am-intro-text">Is this still your class?</p>
              </div>
              <div className="am-card">
                <span className="am-tile">
                  <Icon name="id-card" size={22} />
                </span>
                <span className="am-card-text">
                  <span className="am-card-meta">Your class</span>
                  <span className="am-card-title">{currentClass}</span>
                </span>
              </div>
              {error && (
                <p className="am-note is-danger" role="alert">
                  <Icon name="alert-triangle" size={18} />
                  <span>{error}</span>
                </p>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="am-foot-split">
            <button
              type="button"
              className="am-btn is-soft"
              onClick={() => setChanging(true)}
              disabled={submitting}
            >
              <Icon name="pencil" size={18} />
              Change my class
            </button>
            <button
              type="button"
              className="am-btn is-primary"
              onClick={() => confirmSection(currentSection.section_id)}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="check" size={18} />
                  Yes, that&rsquo;s my class
                </>
              )}
            </button>
          </Modal.Footer>
        </>
      ) : (
      <>
      <Modal.Body>
        <div className="am-stack">
          <div className="am-intro">
            <span className="am-intro-icon">
              <Icon name="id-card" size={28} />
            </span>
            <h3 className="am-intro-title">School year {currentSchoolYear}</h3>
            <p className="am-intro-text">
              Pick your department, grade or program, and section for this
              school year.
            </p>
          </div>

          {currentSection && (
            <p className="am-note">
              <Icon name="history" size={18} />
              <span>
                <b>On record:</b>{" "}
                {currentSection.department_name || "Department"} ·{" "}
                {currentSection.organizational_level_name || "Level"} ·{" "}
                {currentSection.section_name || "Section"}
              </span>
            </p>
          )}

          {error && (
            <p className="am-note is-danger" role="alert">
              <Icon name="alert-triangle" size={18} />
              <span>{error}</span>
            </p>
          )}

          {loading ? (
            <div className="am-intro" role="status">
              <Spinner animation="border" />
              <p className="am-intro-text">Loading options…</p>
            </div>
          ) : (
            <form className="am-form" onSubmit={handleSubmit}>
              <h3 className="am-section-title am-heading">
                <Icon name="layers" size={18} />
                Your class for {currentSchoolYear}
              </h3>

              <div className="am-field">
                <label className="am-label" htmlFor="section-update-department">
                  Department <span className="am-required" aria-hidden="true">*</span>
                </label>
                <span className="am-input">
                  <Icon name="building" size={20} />
                  <select
                    id="section-update-department"
                    value={departmentId}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    required
                  >
                    <option value="">Choose Department...</option>
                    {academicDepartments.length > 0 && (
                      <optgroup label="Academic Departments">
                        {academicDepartments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {adminDepartments.length > 0 && (
                      <optgroup label="Administrative">
                        {adminDepartments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </span>
              </div>

              <div className="am-row">
                <div className="am-field">
                  <label className="am-label" htmlFor="section-update-level">
                    {levelLabel} <span className="am-required" aria-hidden="true">*</span>
                  </label>
                  <span className="am-input">
                    <Icon name="award" size={20} />
                    <select
                      id="section-update-level"
                      value={levelId}
                      onChange={(e) => handleLevelChange(e.target.value)}
                      disabled={!departmentId}
                      required
                    >
                      <option value="" hidden>
                        {!departmentId
                          ? "Select Department First"
                          : `Choose ${levelLabel}...`}
                      </option>
                      {organizationalLevels.map((level) => (
                        <option key={level.id} value={level.id}>
                          {level.name}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>

                <div className="am-field">
                  <label className="am-label" htmlFor="section-update-section">
                    Section <span className="am-required" aria-hidden="true">*</span>
                  </label>
                  <span className="am-input">
                    <Icon name="users" size={20} />
                    <select
                      id="section-update-section"
                      value={sectionId}
                      onChange={(e) => setSectionId(e.target.value)}
                      disabled={!levelId}
                      required
                    >
                      <option value="" hidden>
                        {!levelId
                          ? `Select ${levelLabel} First`
                          : "Choose Section..."}
                      </option>
                      {sections.map((section) => (
                        <option key={section.id} value={section.id}>
                          {section.name}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
              </div>

              {selectedDepartment && selectedLevel && selectedSection && (
                <p className="am-note is-success">
                  <Icon name="check-circle" size={18} />
                  <span>
                    <b>Selected:</b> {selectedDepartment.name} ·{" "}
                    {selectedLevel.name} · {selectedSection.name}
                  </span>
                </p>
              )}
            </form>
          )}
        </div>
      </Modal.Body>

      {!loading && (
        <Modal.Footer className="am-foot-split">
          <p className="am-foot-note">
            <Icon name="shield-check" size={16} />
            Required before you can continue
          </p>
          <button
            type="button"
            className="am-btn is-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                Saving...
              </>
            ) : (
              <>
                <Icon name="check" size={18} />
                Save and Continue
              </>
            )}
          </button>
        </Modal.Footer>
      )}
      </>
      )}
    </Modal>
  );
};

export default SectionUpdateModal;
